import React, { useEffect, useRef, useState } from "react";
import "./NapflixBoot.css";
import {
  BOOT_PHASES,
  getBootState,
  subscribeBootState,
} from "./os/BootManager";

const bootLines = [
  "NAPFLIX BIOS v1.95",
  "Detecting couch-based lifeform...",
  "Spinning up private media signal...",
  "Authenticating snacks-to-screen protocol...",
  "Checking Plex wormhole integrity...",
  "Indexing movies you definitely meant to watch...",
  "Tuning PJTV channels...",
  "Loading forbidden nostalgia modules...",
  "Calibrating remote-control patience...",
  "Mounting Napflix OS...",
  "Welcome back, sleepyhead."
];

const passcode = "••••••••••••";

const bootPhaseLabels = {
  [BOOT_PHASES.BOOT_INIT]: "Initializing NapflixOS...",
  [BOOT_PHASES.NETWORK_WAIT]: "Checking network...",
  [BOOT_PHASES.API_CHECK]: "Connecting to Napflix core...",
  [BOOT_PHASES.PLEX_CHECK]: "Connecting to Plex...",
  [BOOT_PHASES.PJTV_CHECK]: "Loading PJTV channels...",
  [BOOT_PHASES.CACHE_WARM]: "Warming media cache...",
  [BOOT_PHASES.READY]: "Napflix ready.",
  [BOOT_PHASES.DEGRADED]: "Napflix started with warnings.",
};

export default function NapflixBoot({ onDone }) {
  const [phase, setPhase] = useState("boot");
  const [bootState, setBootState] = useState(getBootState());
  const [visibleLines, setVisibleLines] = useState([]);
  const [typedPass, setTypedPass] = useState("");
  const [clock, setClock] = useState("");
  const startupAudioRef = useRef(null);
  const bootPhaseText =
    bootPhaseLabels[bootState.phase] || "Initializing NapflixOS...";

  useEffect(() => {
    fetch("/api/cache/home").catch(() => {});
  }, []);

  useEffect(() => {
    setBootState(getBootState());
    return subscribeBootState(setBootState);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event?.data?.type === "NAPFLIX_SPLASH_DONE") {
        onDone?.();
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onDone]);

  useEffect(() => {
    const tick = () =>
      setClock(
        new Date().toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      );

    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (phase !== "boot") return;

    let i = 0;
    const interval = setInterval(() => {
      setVisibleLines((prev) => [...prev, bootLines[i]]);
      i += 1;

      if (i >= bootLines.length) {
        clearInterval(interval);
        setTimeout(() => setPhase("win95"), 900);
      }
    }, 230);

    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (phase === "win95") {
      const t = setTimeout(() => setPhase("login"), 3300);
      return () => clearTimeout(t);
    }

    if (phase === "login") {
      setTypedPass("");

      const hold = setTimeout(() => {
        let i = 0;
        const typer = setInterval(() => {
          setTypedPass(passcode.slice(0, i + 1));
          i += 1;

          if (i >= passcode.length) {
            clearInterval(typer);
            setTimeout(() => setPhase("ticket"), 1200);
          }
        }, 120);
      }, 2200);

      return () => clearTimeout(hold);
    }

    if (phase === "ticket") {
      window.location.href = "/napflix/custom-intro/napflix_splash.html";
      return;
    }
  }, [phase, onDone]);

  return (
    <div className="napflix-boot">\n      <audio ref={startupAudioRef} src="/ordinary-startup.mp3" preload="auto" />
      {phase === "boot" && (
        <div className="napflix-terminal">
          {visibleLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          <div>{bootPhaseText}</div>
        </div>
      )}

      {phase === "win95" && (
        <div className="napflix-win95">
          <img src="/windows95.jpg" alt="Windows 95" />
        </div>
      )}

      {phase === "login" && (
        <div className="napflix-win-desktop">
          <div className="napflix-desktop-icons">
            <div><img src="/desktop-icons/my-computer.png" alt="" /><span>My Computer</span></div>
            <div><img src="/desktop-icons/recycle-bin.png" alt="" /><span>Recycle Bin</span></div>
            <div><img src="/desktop-icons/napster.png" alt="" /><span>Napster</span></div>
            <div><img src="/desktop-icons/winamp.png" alt="" /><span>Winamp</span></div>
            <div><img src="/desktop-icons/aim.png" alt="" /><span>AIM</span></div>
          </div>

          <div className="napflix-browser-window">
            <div className="napflix-titlebar">
              <span>Napflix —</span>
              <div><button>_</button><button>□</button><button>×</button></div>
            </div>

            <div className="napflix-menubar">
              <span>File</span><span>Edit</span><span>View</span><span>Favorites</span><span>Help</span>
            </div>

            <div className="napflix-addressbar">
              <span>Address</span>
              <div>napflix.live</div>
              <button>Go</button>
            </div>

            <div className="napflix-window-logo">
              <img src="/napflix/napflix-logo-splash.png" alt="Napflix" />
            </div>

            <div className="napflix-passcode">
              <strong>Enter Passcode to Join:</strong>
              <div className="napflix-password">{typedPass}</div>
              <div className="napflix-buttons">
                <button>Connect</button>
                <button>Cancel</button>
              </div>
            </div>

            <div className="napflix-statusbar">
              <span>{bootPhaseText}</span><span>Protect</span><span>Trust</span><span>powered by icq</span>
            </div>
          </div>

          <div className="napflix-taskbar">
            <button><span className="napflix-start-flag"><i></i><i></i><i></i><i></i></span> Start</button>
            <div className="napflix-taskbar-clock">{clock}</div>
          </div>
        </div>
      )}

      {phase === "ticket" && (
        <div className="napflix-ticket-sequence" />
      )}
    </div>
  );
}
